import * as THREE from "three/build/three.module";
// UA-based mobile flag from react-device-detect — governs sketch branching
// (bulb scale factor, canvas sizing).  Home.js uses a separate viewport-width
// check (checkIsMobile) for touch-scroll handler registration; the two are
// intentionally distinct and should not be conflated.
import { isMobile } from "react-device-detect";
import { getGPUTier } from "detect-gpu";
import Audio from "./modules/Audio";

// import Stats from 'three/examples/jsm/libs/stats.module';

import gsap from "gsap";

import PostProcessing from "./modules/PostProcessing";
import { ImprovedNoise } from "./modules/ImprovedNoise";
// import ParticleSystem from './modules/ParticleSystem';
import SoulEater from "./modules/DarkMatter";

import workList from "../../../data/workList.json";
import { getLabState } from "../../../lib/labBackground";
// Phase 8 tuning scaffold (TEMPORARY — remove at sign-off): live aesthetic levers + dev overlay.
import labTuning from "../../../lib/labTuning";
import { mountLabTunePanel } from "../../../lib/labTunePanel";

// const ENTIRE_SCENE = 0;
const BLOOM_SCENE = 1;

const TARGET_FPS = 60;
const FRAME_INTERVAL = 1000 / TARGET_FPS; // ~16.67 ms

// Scales how much the audio signal adds to light intensity.
// 1.0 = natural audio contribution; increase for a more reactive look.
const AUDIO_LIGHT_MULTIPLIER = 1.0;

// Scales how much the audio signal adds to the light bulb visual size.
// Independent from AUDIO_LIGHT_MULTIPLIER so both can be tuned separately.
const AUDIO_BULB_MULTIPLIER = 1.0;

// Scales how much the audio signal adds to the bloom strength.
const AUDIO_BLOOM_MULTIPLIER = 1.0;

// Transient "completion burst" on manifesto entry (Press & Hold release into
// manifesto). A short scalar _burst (0→1→0, GSAP-driven in enterManifestoFromHero)
// is multiplied by these to flare bloom and surge particle mass/scale, giving the
// moment a physical "ignition + residue" feel. Additive on top of the normal drive.
const BURST_BLOOM = 1.2;
const BURST_MASS = 120.0;
const BURST_PARTICLE_SCALE = 0.6;

// Base LAB curl drift strength (Phase 3A). Zeroed under reduced motion so the LAB
// field is calm/static. Only affects the velocity shader when uLabBlend > 0.
// Reduced 3.0 -> 1.5 (Phase 3B stabilization): at 3.0 the curl saturated the velocity
// clamp and dispersed the field; lower lets LAB_CONTAINMENT steer drift back toward home.
// Phase 8 (TEMPORARY): LAB curl drift strength is tuned live via labTuning.flowStrength;
// baked back to `const LAB_FLOW_STRENGTH = <value>;` at sign-off. Default 1.5.

// LAB per-item focus shaping (Phase 6). The active item's most distinctive category eases a
// steady multiplier onto LAB_FLOW_STRENGTH: AI → more flow (diffuse/unstable), TOOLS → less
// (calm/structured), WEB/neutral → 1.0. SPAN sets the range: focusEnergy [0..1] → mul
// [1 - SPAN/2 .. 1 + SPAN/2] = [0.7 .. 1.3]. 0 disables the effect.
const LAB_FOCUS_FLOW_SPAN = 0.6;

// LAB filter reformation (Phase 7). On a filter change (kind-2 pulse) the curl flow first DIPS
// (flow ×(1 − CONTRACT) at impulse peak) so the existing velocity-shader containment spring
// dominates and visibly draws the field toward its nebula band; the dip decays (REFORM_DECAY,
// ≈0.23 s half-life) faster than the kind-2 pulse (0.03), so flow then rises through baseline
// into a brief controlled wake before settling. SPREAD_SPAN scales the steady set-size lean:
// flow ×[1 − SPAN .. 1] across set ratios [0 .. 1] (small filtered sets sit slightly calmer/
// tighter). Either axis is disabled by setting its constant to 0.
const LAB_REFORM_CONTRACT = 0.85;
const LAB_REFORM_DECAY = 0.05;
const LAB_SPREAD_SPAN = 0.12;

// LAB interaction-energy gains (Phase 3C). The LAB→background bridge drives a drag
// energy + pulse energy scalar that scales the curl flow strength: dragging makes the
// field more turbulent; filter/active changes inject a decaying reformation pulse.
const LAB_DRAG_GAIN = 1.8;
const LAB_PULSE_GAIN = 1.2;
// Interaction energy also raises the velocity clamp (max particle speed). The curl
// velocity is clamped by velocityClamp, so scaling flow amplitude alone saturates and
// is invisible — the speed cap is the lever that actually makes drag/pulse felt.
const LAB_CLAMP_BOOST = 12.0;

class Sketch {
  /**
   * @param {HTMLElement} mount  - DOM element the canvas is appended to.
   * @param {Function}   [onReady] - Optional callback fired just before the
   *   first animate() call, once async GPU detection + init have completed.
   *   Use it to set show/animating flags so the first animate() starts live.
   */
  constructor(mount, onReady) {
    this.mount = mount;
    this.mousePosition = new THREE.Vector2(0.5, 0.5);
    this.pMousePosition = new THREE.Vector2(0.5, 0.5);
    this.mouse = new THREE.Vector3(0.0, 0.0, 0.0);
    this.pMouse = new THREE.Vector3(0.0, 0.0, 0.0);
    this.lastPointerClientX = window.innerWidth * 0.5;
    this.lastPointerClientY = window.innerHeight * 0.5;
    this.topy = { value: 0.0 };
    this.time = Math.random() * 120000;
    this.bulbScale = { value: 1 };
    this.holdProgress = 0;
    this._holdScaleActive = false;
    // Transient manifesto-entry burst scalar (0 at rest). Tweened 0→1→0 in
    // enterManifestoFromHero; read additively in the animate loop. Initialized
    // here so the loop expression is always safe even before any burst fires.
    this._burst = 0;
    this.show = false;

    this.noise = new ImprovedNoise();

    this.pixelRatio = 1;
    this.animating = false;
    this.isManifesto = false;
    this.isManifestoPlaying = false;
    // Section-aware background mode. 'default' = site-wide mouse-follow gravity (current
    // behavior); 'lab' = LAB autonomous field. Drives the uLabBlend ramp in update().
    this.sectionMode = 'default';
    // Cached reduced-motion query — drives the LAB curl reduced-motion gate in update()
    // (zeroes flow strength + snaps the blend so the LAB field stays calm/static).
    this._reduceMotionMQL = window.matchMedia('(prefers-reduced-motion: reduce)');
    // LAB interaction energies (Phase 3C). Ramped from the labBackground bridge in update();
    // hard-reset to 0 outside LAB so no stale drag/pulse turbulence survives a re-entry.
    this._labDragEnergy = 0;
    this._labPulseEnergy = 0;
    this._labLastPulseSeq = 0;
    // LAB per-item focus lean (Phase 6). Eased toward the active item's category profile in
    // update(); 0.5 = neutral. Reset to neutral outside LAB. Applied only when uLabBlend > 0.
    this._labFocusEnergy = 0.5;
    // LAB filter reformation (Phase 7). reformEnergy: one-shot contraction impulse set on a
    // kind-2 (filter) pulse — dips the curl flow so the containment spring draws the field
    // toward its nebula homes (tighten → wake → settle); decays faster than the pulse.
    // spreadEnergy: eased toward the filtered set's relative size (rest 1 = full archive).
    this._labReformEnergy = 0;
    this._labSpreadEnergy = 1;

    this._mouseMoveHandler = null; // stored for clean removal
    this._onReady = onReady || null;
    this._lastFrameTime = 0;

    this.audio = new Audio(this.manifestoIsNotPlaying);

    // this.audio.init().then(() => {
    (async () => {
      const gpuTier = await getGPUTier();

      if (this.init(gpuTier)) {
        if (this._onReady) this._onReady(this);
        this.animate();
      }
    })();
    // });
  }

  init = gpuTier => {
    this.bulbpos = new THREE.Vector3(
      Math.random(),
      Math.random(),
      Math.random()
    );

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("webgl2", { antialias: true });
    this.renderer = new THREE.WebGLRenderer({ canvas, context });


    // console.log(GPUTier.tier);

    let darkMatterResolution = 64;
    if (gpuTier.tier === "GPU_DESKTOP_TIER_1") {
      // if (renderer.includes('amd') || renderer.includes('nvidia')) darkMatterResolution = 64;
      // else
      darkMatterResolution = 96;
    } else if (gpuTier.tier === "GPU_MOBILE_TIER_4") {
      darkMatterResolution = 96;
    } else if (gpuTier.tier === "GPU_MOBILE_TIER_3") {
      darkMatterResolution = 64;
    } else if (gpuTier.tier === "GPU_MOBILE_TIER_2") {
      darkMatterResolution = 32;
    } else if (gpuTier.tier === "GPU_MOBILE_TIER_1") {
      darkMatterResolution = 16;
    }

    this.lightSize = window.innerWidth / 4;
    if (isMobile) this.lightSize = window.innerWidth;

    if (
      this.renderer.capabilities.isWebGL2 === false &&
      this.renderer.extensions.get("ANGLE_instanced_arrays") === null
    ) {
      return false;
    }

    this.bloomLayer = new THREE.Layers();
    this.bloomLayer.set(BLOOM_SCENE);

    this.camerapos = new THREE.Vector3(
      Math.random(),
      Math.random(),
      Math.random()
    );

    this.darkMaterial = new THREE.MeshBasicMaterial({
      color: "black",
      opacity: 0.0,
    });
    this.materials = {};

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      10000000
    );
    this.camera.position.z = window.innerHeight * 0.875;

    this.renderer.setPixelRatio(this.pixelRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    // this.renderer.autoClear = false;
    this.renderer.setClearColor(0x060606);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    // use ref as a mount point of the Three.js scene instead of the document.body
    this.mount.appendChild(this.renderer.domElement);

    // this.scene.add(new THREE.AmbientLight(0xffffff));

    this.post_effect = new PostProcessing(
      window.innerWidth,
      window.innerHeight,
      this.renderer,
      this.scene,
      this.camera
    );

    const w_ = 500;

    this.light = new THREE.PointLight(0xffffff, 0.75, 75);
    this.light.distance = this.lightSize;
    this.light.position.copy(this.mouse);
    this.lightBulb = new THREE.IcosahedronGeometry(10 * this.pixelRatio, 1);
    this.lightMaterial = new THREE.MeshBasicMaterial({
      color: 0xaaaaaa,
      side: THREE.DoubleSide,
      depthWrite: false,
      depthTest: false,
    });
    this.lightMesh = new THREE.Mesh(this.lightBulb, this.lightMaterial);
    this.lightMesh.frustumCulled = false;
    this.lightMesh.layers.enable(BLOOM_SCENE);
    this.light.add(this.lightMesh);
    this.scene.add(this.light);

    const envWidth = window.innerWidth * 2;
    const envHeight = isMobile ? window.innerWidth * 2 : window.innerWidth;

    this.envGeometry = new THREE.BoxBufferGeometry(envWidth, envHeight, 1);
    this.envMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      emissiveIntensity: 1.0,
      metalness: 1.0,
      // side: THREE.BackSide,
      lightMapIntensity: 1.0,
      depthWrite: false,
      depthTest: false,
    });
    this.envMesh = new THREE.Mesh(this.envGeometry, this.envMaterial);
    this.envMesh.position.set(0, 0, envHeight);
    // this.envMesh.layers.enable(ENTIRE_SCENE);
    this.scene.add(this.envMesh);

    this.darkMatterParticles = new SoulEater(
      50,
      w_ / 4,
      darkMatterResolution,
      this.renderer
    );
    // this.darkMatterParticles.mesh.layers.enable(ENTIRE_SCENE);
    this.scene.add(this.darkMatterParticles.mesh);

    // Phase 8 tuning scaffold (TEMPORARY — remove at sign-off): mount the dev tuning panel only when
    // the ?labtune flag is in the URL. Never mounts otherwise / in production.
    if (typeof window !== 'undefined' && window.location && window.location.search.indexOf('labtune') !== -1) {
      mountLabTunePanel();
    }

    // this.stats = new Stats();
    // document.body.appendChild(this.stats.dom);

    // if (!isMobile) {
    if (!isMobile) {
      this._mouseMoveHandler = (e) => this.mouseHandler(e);
      window.addEventListener("mousemove", this._mouseMoveHandler);
    }
    // } else {
    //     this.mouse = new THREE.Vector3(window.innerWidth * 0.75, window.innerHeight * 0.25, 0.0);
    //     this.mousePosition = new THREE.Vector2(0.75, 0.25);
    // }

    window.addEventListener("resize", this.resizeHandler);

    return true;
  };

  resizeHandler = () => {
    const { width, height } = this.getViewportSize();

    this.renderer.setSize(width, height);
    this.renderer.setViewport(0, 0, width, height);

    this.camera.aspect = width / height;
    this.camera.position.z = height * 0.875;
    this.camera.updateProjectionMatrix();

    this.lightSize = isMobile ? width : width / 4;

    if (this.darkMatterParticles) {
      if (this.darkMatterParticles.positionUniforms?.screenResolution) {
        this.darkMatterParticles.positionUniforms.screenResolution.value.set(width, height);
      }
      if (this.darkMatterParticles.velocityUniforms?.screenResolution) {
        this.darkMatterParticles.velocityUniforms.screenResolution.value.set(width, height);
      }
      if (this.darkMatterParticles.velocityUniforms?.center) {
        this.darkMatterParticles.velocityUniforms.center.value.set(width * 0.5, height * 0.5);
      }
    }

    this.post_effect.resize(width, height);

    this.syncMouseFromClient(
      this.lastPointerClientX ?? width * 0.5,
      this.lastPointerClientY ?? height * 0.5,
      true
    );
  };

  handleSections = (n) => {
    if (!isMobile) {
      if (n === 0 || n === 1 || n === 2 || n === 3) {
        // Always set the animating flag. Only start the loop if it is not
        // already running — checking show prevents a second setTimeout chain.
        // Guard: only call animate() if async init has completed (darkMatterParticles
        // exists). If handleSections fires before init, the constructor's own
        // animate() call will start the loop once init finishes.
        this.animating = true;
        if (!this.show) {
          this.show = true;
          if (this.darkMatterParticles) this.animate();
        }
        gsap.to(this.topy, { value: 0, duration: 2.0, ease: "power4.inOut" });
        // isManifesto is now exclusively controlled by the hero hold gesture
        // (enterManifestoFromHero / exitManifestoFromHero). All scroll sections
        // keep the sketch in quiescent state.
        this.isManifesto = false;
        gsap.to(this.bulbScale, {
          value: 1,
          duration: 2.0,
          ease: "power4.inOut",
        });
      }
    } else {
      // Mobile: sketch is not shown during scroll navigation.
      this.show = false;
      this.animating = false;
      this.isManifesto = false;
      gsap.to(this.bulbScale, {
        value: 1,
        duration: 2.0,
        ease: "power4.inOut",
      });
    }
  };

  /**
   * Set the section-aware background mode (additive; mirrors the isManifesto flag).
   * Phase 1: only records the mode; update() lerps the inert uLabBlend uniform toward
   * it. uLabBlend is not yet read by the velocity shader, so any value is byte-identical
   * to current output. Later phases wire it (+ further uniforms) into the shader for
   * autonomous LAB motion. Safe to call before async init completes — it sets only a
   * scalar field; the uniform write happens in update(), guarded by darkMatterParticles.
   * @param {'lab'|'default'} mode
   */
  setSectionMode = (mode) => {
    this.sectionMode = mode === 'lab' ? 'lab' : 'default';
  };

  lerp = (start, stop, amt) => {
    return amt * (stop - start) + start;
  };

  getViewportSize = () => {
    return {
      width: this.mount?.clientWidth || window.innerWidth,
      height: this.mount?.clientHeight || window.innerHeight
    };
  };

  syncMouseFromClient = (clientX, clientY, immediate = false) => {
    const { width, height } = this.getViewportSize();
    const x = Math.max(0, Math.min(width, clientX));
    const y = Math.max(0, Math.min(height, clientY));
    this.pMousePosition.x = x / width;
    this.pMousePosition.y = 1.0 - y / height;
    this.pMouse.x = x;
    this.pMouse.y = height - y;
    this.pMouse.z = 0.0;
    if (immediate) {
      this.mouse.copy(this.pMouse);
      this.mousePosition.copy(this.pMousePosition);
    }
  };

  mouseHandler = (e) => {
    if (!this.isManifesto) {
      this.lastPointerClientX = e.clientX;
      this.lastPointerClientY = e.clientY;
      this.syncMouseFromClient(e.clientX, e.clientY, false);
    }
  };

  setVolume = (volume) => {
    this.audio.setVolume(volume);
  };

  getAudioCurrentTime = () => {
    if (!this.audio || !this.audio.audio) return 0;
    return this.audio.audio.currentTime;
  };

  playManifesto = () => {
    this.audio.play();
    this.isManifestoPlaying = true;
  };

  stopManifesto = () => {
    if (this.audio) {
      this.audio.pause();
    }
  };

  manifestoIsNotPlaying = () => {
    this.isManifestoPlaying = false;
  };

  update = () => {
    if (this.animating) {
      const volumeMultiplier = 10;
      const rawMeterVolume = this.audio.meter ? this.audio.meter.volume : 0.0;

      // Gate on audio.isPlaying, not rawMeterVolume < 0.01. The ScriptProcessor
      // meter starts at volume=0 for at least the first 512-sample buffer, so
      // the old check was killing isManifestoPlaying on the very first frame and
      // locking audioBoost at 0 for the entire playback.
      if (this.isManifestoPlaying && !this.audio.isPlaying)
        this.isManifestoPlaying = false;

      // audioBoost is 0 when not playing; additive over base values.
      const audioBoost = this.audio.isPlaying
        ? rawMeterVolume * volumeMultiplier * AUDIO_LIGHT_MULTIPLIER
        : 0.0;

      if (!this.isManifesto) {
        const targetX = this.lerp(this.pMouse.x, window.innerWidth / 2, this.holdProgress);
        const targetY = this.lerp(this.pMouse.y, window.innerHeight / 2, this.holdProgress);
        const targetPosX = this.lerp(this.pMousePosition.x, 0.5, this.holdProgress);
        const targetPosY = this.lerp(this.pMousePosition.y, 0.5, this.holdProgress);
        this.mouse.x = this.lerp(this.mouse.x, targetX, 0.1);
        this.mouse.y = this.lerp(this.mouse.y, targetY, 0.1);
        this.mouse.z = 0.0;
        this.mousePosition.x = this.lerp(this.mousePosition.x, targetPosX, 0.1);
        this.mousePosition.y = this.lerp(this.mousePosition.y, targetPosY, 0.1);
      } else {
        const chaosTime = performance.now() * 0.001;
        const nx =
          this.noise.noise(
            this.camerapos.x + chaosTime,
            this.camerapos.y,
            this.camerapos.z
          ) * 10;
        const ny =
          this.noise.noise(
            this.camerapos.x,
            this.camerapos.y + chaosTime,
            this.camerapos.z
          ) * 15;
        const nz =
          this.noise.noise(
            this.camerapos.x,
            this.camerapos.y,
            this.camerapos.z + chaosTime
          ) * 20;
        this.mouse.x = this.lerp(this.mouse.x, window.innerWidth / 2, 0.1) + nx;
        this.mouse.y =
          this.lerp(this.mouse.y, window.innerHeight / 2, 0.1) + ny;
        this.mouse.z = nz;
        this.mousePosition.x = this.lerp(this.mousePosition.x, 0.5, 0.1);
        this.mousePosition.y = this.lerp(this.mousePosition.y, 0.5, 0.1);
      }

      this.camera.position.y = this.topy.value;

      // Drive bulb scale, gravity, and particle scale from holdProgress.
      // bulbScale is only updated here while a hold is active; once the hold
      // completes (enterManifestoFromHero) or is unwound (deactivateForHold
      // onComplete), _holdScaleActive is cleared so GSAP can own bulbScale.
      if (this._holdScaleActive) {
        const scaleTarget = isMobile ? 6 : 10;
        this.bulbScale.value = this.lerp(1.0, scaleTarget, this.holdProgress);
      }
      if (this.darkMatterParticles) {
        this.darkMatterParticles.velocityUniforms['originMass'].value =
          this.lerp(20.0, 200.0, this.holdProgress) + this._burst * BURST_MASS;
        this.darkMatterParticles.velocityUniforms['velocityClamp'].value =
          this.lerp(5.0, 12.0, this.holdProgress);
        // Phase 8 (TEMPORARY): LAB-gated particle-scale mix. _bb is 0 outside LAB → Home identical.
        {
          const _bb = this.darkMatterParticles.velocityUniforms['uLabBlend'].value;
          const _ps = this.lerp(1.0, 2.0, this.holdProgress) + this._burst * BURST_PARTICLE_SCALE;
          this.darkMatterParticles.material.uniforms.particleScale.value =
            _ps * (1.0 - _bb) + labTuning.particleScale * _bb;
        }
        // LAB curl drift (Phase 3A): ramp the LAB-mode blend toward its target and drive
        // the curl strength. Reduced motion → snap the blend (no ramp motion) and zero the
        // flow so the LAB field is calm/static.
        const labRM = this._reduceMotionMQL && this._reduceMotionMQL.matches;
        const labTarget = this.sectionMode === 'lab' ? 1.0 : 0.0;
        const labBlend = this.darkMatterParticles.velocityUniforms['uLabBlend'];
        labBlend.value = this.lerp(labBlend.value, labTarget, labRM ? 1.0 : 0.06);
        // REQUIRED: the asymptotic lerp never reaches exactly 0/1. Snap to the target so
        // the shader's `if (uLabBlend > 0.0)` branch truly turns OFF after leaving LAB —
        // otherwise a tiny residual >0 keeps the curl branch live and Home/Work/About would
        // not return to byte-exact default behavior.
        if (labTarget === 0.0 && labBlend.value < 0.001) labBlend.value = 0.0;
        else if (labTarget === 1.0 && labBlend.value > 0.999) labBlend.value = 1.0;
        // LAB interaction energy (Phase 3C): the labBackground bridge feeds drag + pulse
        // energy that scales the curl flow strength (drag → turbulence; filter/active →
        // reformation pulse). Hard-reset to 0 outside LAB so no stale energy survives a
        // re-entry; the uLabBlend shader gate + the RM gate below keep this invisible when
        // it should be (default byte-identical at blend 0; calm/static under reduced motion).
        const lab = getLabState();
        if (!lab.active) {
          this._labDragEnergy = 0.0;
          this._labPulseEnergy = 0.0;
          this._labLastPulseSeq = lab.pulseSeq;
          this._labFocusEnergy = 0.5; // Phase 6: neutral lean outside LAB (no stale focus on re-entry)
          this._labReformEnergy = 0.0; // Phase 7: no stale reformation dip on re-entry
          this._labSpreadEnergy = 1.0; // Phase 7: rest spread outside LAB
        } else {
          const dragTarget = lab.dragging ? 1.0 : 0.0;
          // Asymmetric: fast attack on grab so even a brief belt drag registers, slow
          // release so the turbulence lingers into a settling wake.
          const dragRate = lab.dragging ? 0.14 : 0.05;
          this._labDragEnergy = this.lerp(this._labDragEnergy, dragTarget, dragRate);
          if (lab.pulseSeq !== this._labLastPulseSeq) {
            this._labLastPulseSeq = lab.pulseSeq;
            const mag = lab.pulseKind >= 2 ? 1.0 : 0.55; // filter change > active change
            if (mag > this._labPulseEnergy) this._labPulseEnergy = mag;
            // Phase 7: a filter change (kind 2) also fires the reformation contraction (beat 1).
            if (lab.pulseKind >= 2) this._labReformEnergy = 1.0;
          }
          this._labPulseEnergy = this.lerp(this._labPulseEnergy, 0.0, 0.03); // slow decay → lingering reformation
          // Phase 6: ease toward the active item's category lean (steady, distinct from the
          // transient drag/pulse energy above). Slow lerp so navigating items shifts the
          // field's flow energy over ~1s rather than snapping.
          this._labFocusEnergy = this.lerp(this._labFocusEnergy, lab.focusDiffusion, 0.04);
          // Phase 7: decay the reformation dip (faster than the kind-2 pulse above, so the
          // wake emerges as the dip releases) and ease toward the filtered set's spread.
          this._labReformEnergy = this.lerp(this._labReformEnergy, 0.0, LAB_REFORM_DECAY);
          this._labSpreadEnergy = this.lerp(this._labSpreadEnergy, lab.filterSpread, 0.04);
        }
        const labEnergy = this._labDragEnergy * LAB_DRAG_GAIN + this._labPulseEnergy * LAB_PULSE_GAIN;
        // Phase 6: per-item focus multiplier on the base flow. focusEnergy 0.5 → focusMul 1.0
        // (identical to pre-Phase-6); < 0.5 calms the field (TOOLS), > 0.5 energizes it (AI).
        const focusMul = 1.0 + (this._labFocusEnergy - 0.5) * LAB_FOCUS_FLOW_SPAN;
        // Phase 7: reformation dip (beat 1 — quiets the curl so the containment spring draws
        // the field home) + steady set-size lean. Both 1.0 at rest; hard-reset outside LAB.
        const reformMul = 1.0 - LAB_REFORM_CONTRACT * this._labReformEnergy;
        const spreadMul = 1.0 - LAB_SPREAD_SPAN * (1.0 - this._labSpreadEnergy);
        this.darkMatterParticles.velocityUniforms['uLabFlowStrength'].value =
          labRM ? 0.0 : labTuning.flowStrength * focusMul * spreadMul * reformMul * (1.0 + labEnergy);
        // Raise the speed cap with the interaction energy so drag/pulse are actually felt:
        // the curl velocity is clamped by velocityClamp, so flow amplitude alone saturates.
        // labEnergy is 0 outside LAB (hard reset above) and forced 0 under RM, so the
        // default path and reduced motion keep their existing velocityClamp untouched.
        this.darkMatterParticles.velocityUniforms['velocityClamp'].value +=
          labRM ? 0.0 : labEnergy * LAB_CLAMP_BOOST;
        // LAB lifecycle reduced-motion flag (Phase 3B). DarkMatter.update() fans this out
        // to the position (respawn/aging) + render (fade) materials. RM freezes the lifecycle.
        this.darkMatterParticles.velocityUniforms['uLabReducedMotion'].value = labRM ? 1.0 : 0.0;
      }

      const time = this.time + performance.now() * 0.25;

      const nt = time * 0.001;

      this.darkMatterParticles.update(
        time * 0.025,
        this.mouse,
        this.isManifesto || this.isManifestoPlaying
      );

      // Base light intensity is constant; audio adds on top additively.
      // Phase 8 (TEMPORARY): LAB-gated light intensity mix (_bb 0 outside LAB → Home identical).
      {
        const _bb = this.darkMatterParticles.velocityUniforms['uLabBlend'].value;
        this.light.intensity = (2.0 + audioBoost) * (1.0 - _bb) + labTuning.lightIntensity * _bb;

        // LAB: blend the bulb off the mouse toward the composition center (origin) so the field
        // reads as autonomous, not a cursor toy. The mouse-follow target is (mouse - W/2, mouse - H/2),
        // whose centered counterpart is exactly (0,0) — so scaling by (1 - _bb) IS the mix. uLabBlend
        // already eases in/out, giving a smooth transition; at _bb === 0 the mouse path is byte-identical,
        // so Home/Work/About/Manifesto are unchanged. (Light POSITION is centered by design, not a tunable.)
        this.light.position.x = (this.mouse.x - window.innerWidth / 2) * (1.0 - _bb);
        this.light.position.y = (this.mouse.y - window.innerHeight / 2) * (1.0 - _bb);
        this.light.position.z = 0.0;
      }

      this.light.distance =
        this.lightSize * Math.max(1.0, this.bulbScale.value * 0.2);

      this.lightMesh.scale.x = this.bulbScale.value + audioBoost * AUDIO_BULB_MULTIPLIER;
      this.lightMesh.scale.y = this.bulbScale.value + audioBoost * AUDIO_BULB_MULTIPLIER;
      this.lightMesh.scale.z = this.bulbScale.value + audioBoost * AUDIO_BULB_MULTIPLIER;

      this.light.target = this.bulb;

      // Phase 8 (TEMPORARY): LAB-gated bloom. Strength scaled by the tuning factor; radius + threshold
      // mixed toward the LAB values (decoupled from strength for soft glow). _bb 0 → Home identical
      // (radius keeps the strength-derived value, threshold keeps the 0.35 default).
      {
        const _bb = this.darkMatterParticles.velocityUniforms['uLabBlend'].value;
        const _sBase = 1.0 + audioBoost * AUDIO_BLOOM_MULTIPLIER + this._burst * BURST_BLOOM;
        const _s = _sBase * (1.0 - _bb) + (_sBase * labTuning.bloomStrength) * _bb;
        this.post_effect.update(1000.0 + nt, this.mousePosition, _s);
        this.post_effect.bloomPass.radius =
          this.post_effect.bloomPass.radius * (1.0 - _bb) + labTuning.bloomRadius * _bb;
        this.post_effect.bloomPass.threshold = 0.35 * (1.0 - _bb) + labTuning.bloomThreshold * _bb;
      }

      // this.stats.update();

      // console.log('lol')
    } else {
      const time = this.time + performance.now() * 0.25;
      const nt = time * 0.001;
      this.post_effect.update(1000.0 + nt, this.mousePosition, 1.0);
    }
  };

  animate = (timestamp = 0) => {
    if (this.show) {
      this._rafId = requestAnimationFrame(this.animate);

      // Cap render work to TARGET_FPS. RAF keeps firing at the display rate
      // so frame-timing stays accurate; we just skip render on frames that
      // arrive too early. The modulo prevents drift on slow frames.
      const elapsed = timestamp - this._lastFrameTime;
      if (elapsed < FRAME_INTERVAL) return;
      this._lastFrameTime = timestamp - (elapsed % FRAME_INTERVAL);

      if (this.animating) this.update();

      this.darkMatterParticles.getDark();
      this.post_effect.bloomComposer.render();
      this.darkMatterParticles.restoreColor();
      // Phase 8 (TEMPORARY): LAB-gated particle monochrome value. restoreColor() resets pColor to 0.5
      // every frame, so outside LAB this override is skipped → Home identical.
      {
        const _bb = this.darkMatterParticles.velocityUniforms['uLabBlend'].value;
        if (_bb > 0.0) {
          const _v = 0.5 * (1.0 - _bb) + labTuning.pColorValue * _bb;
          this.darkMatterParticles.material.uniforms.pColor.value.set(_v, _v, _v);
        }
      }
      this.post_effect.finalComposer.render();
    }
  };

  darkenNonBloomed = (obj) => {
    if (obj.isMesh && this.bloomLayer.test(obj.layers) === false) {
      if (obj.name !== "DarkMatterParticles") {
        this.materials[obj.uuid] = obj.material;
        obj.material = this.darkMaterial;
      }
    }
  };

  restoreMaterial = (obj) => {
    if (this.materials[obj.uuid] && obj.name !== "DarkMatterParticles") {
      obj.material = this.materials[obj.uuid];
      delete this.materials[obj.uuid];
    }
  };

  getSelectedWork = (n) => {
    if (Number.isInteger(n)) {
      const color = new THREE.Color(workList[n].color);
      gsap.to(this.light.color, 1, { r: color.r, g: color.g, b: color.b });
      gsap.to(this.lightMaterial.color, 1, {
        r: color.r,
        g: color.g,
        b: color.b,
      });
    }
  };

  /**
   * Start (or resume) rendering. Safe to call at any time after construction.
   * When called from an onReady callback (before animate()), only the flags
   * are set — the init chain calls animate() immediately after.
   * When called after init (e.g. route change), also kicks off the loop.
   */
  activate = () => {
    if (!this.show) {
      this.show = true;
      this.animating = true;
      // Only call animate() ourselves when the loop is not already running.
      // If we were called from onReady, init will call animate() right after.
      if (this.darkMatterParticles) {
        this.animate();
      }
    }
  };

  /**
   * Temporarily activate the sketch while the user holds on section 0.
   * No-op on mobile (sketch only activates for section 3 there).
   * Kills any pending deactivateForHold tween so rapid re-holds are safe.
   */
  activateForHold = () => {
    if (isMobile) return;
    gsap.killTweensOf(this);
    gsap.killTweensOf(this.bulbScale);
    this.holdProgress = 0;
    this._holdScaleActive = true;
    if (!this.animating) this.animating = true;
    if (!this.show) {
      this.show = true;
      this.animate();
    }
  };

  /**
   * Drive the light drift during a hold. Called every RAF tick by HomeHero.
   * @param {number} p  Progress value in [0, 1].
   */
  setHoldProgress = (p) => {
    this.holdProgress = p;
  };

  /**
   * Enter canonical manifesto mode triggered from the hero hold interaction.
   * Sets isManifesto=true and swells bulbScale to the desktop manifesto value.
   * Does NOT start audio — caller (Home.js activateManifestoFromHero) calls
   * playManifesto() separately.
   * No-op on mobile (sketch only activates at section 3 there).
   */
  enterManifestoFromHero = () => {
    if (isMobile) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Kill any pending deactivateForHold tween so rapid activation is safe.
    gsap.killTweensOf(this);
    this._holdScaleActive = false;
    this.isManifesto = true;
    // Completion burst: quick flare up, slow ease out. Additive bloom + particle
    // surge read in the animate loop. A later exit/abort calls gsap.killTweensOf(this),
    // which also stops an in-flight burst tween, so no extra cleanup is needed.
    this._burst = 0;
    gsap.to(this, {
      _burst: 1,
      duration: 0.18,
      ease: 'power2.out',
      onComplete: () => gsap.to(this, { _burst: 0, duration: 0.9, ease: 'power2.out' }),
    });
    gsap.to(this.bulbScale, { value: 10, duration: 2.0, ease: 'power4.inOut' });
  };

  /**
   * Exit hero-triggered manifesto mode on hold release.
   * Restores isManifesto=false, tweens bulbScale back to 1, then delegates
   * to deactivateForHold() to tween holdProgress → 0 and stop the render loop.
   * No-op on mobile.
   */
  exitManifestoFromHero = () => {
    if (isMobile) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.isManifesto = false;
    this.isManifestoPlaying = false;
    // Fast elastic snap back — 0.35s so the bulb closes before the canvas stops.
    // overwrite:true kills the in-flight enterManifestoFromHero bulbScale→10 tween
    // which would otherwise resume and push the bulb back to 10 after this completes.
    gsap.to(this.bulbScale, { value: 1, duration: 0.35, ease: 'power2.in', overwrite: true });
    // 0.65s keeps the canvas alive until the background finishes fading (0.6s)
    // so particles don't visibly freeze while the background is still opaque.
    this.deactivateForHold(0.65);
  };

  /**
   * Restore quiescent section-0 state after a hold is aborted.
   * Tweens holdProgress back to 0 then stops the render loop.
   * No-op on mobile.
   *
   * @param {number} [duration=0.4]  Duration of the holdProgress→0 tween.
   *   Pre-threshold path (HomeHero cancelHold) uses the default 0.4s.
   *   Post-threshold path (exitManifestoFromHero) passes 0.65s to keep the
   *   canvas alive until the background opacity fade (0.6s) completes.
   */
  deactivateForHold = (duration = 0.4) => {
    if (isMobile) return;
    gsap.killTweensOf(this);
    // Only tween holdProgress back to 0 — section 0 stays live for idle
    // mouse-follow. Callers (cancelHold abort path, exitManifestoFromHero)
    // control background opacity separately via setSiteBackgroundOpacity.
    gsap.to(this, {
      holdProgress: 0,
      duration,
      ease: 'power2.out',
      onComplete: () => { this._holdScaleActive = false; },
    });
  };

  /**
   * Tear down event listeners and dispose Three.js resources.
   * Called when the SiteBackground component unmounts.
   */
  destroy = () => {
    this.show = false;
    this.animating = false;
    cancelAnimationFrame(this._rafId);
    this._rafId = null;
    if (this.audio) {
      this.audio.close();
    }
    window.removeEventListener("resize", this.resizeHandler);
    if (this._mouseMoveHandler) {
      window.removeEventListener("mousemove", this._mouseMoveHandler);
      this._mouseMoveHandler = null;
    }
    if (this.renderer) {
      this.renderer.dispose();
      if (
        this.mount &&
        this.renderer.domElement.parentNode === this.mount
      ) {
        this.mount.removeChild(this.renderer.domElement);
      }
    }
  };
}

// window.addEventListener('resize', function () {
//     const width = window.innerWidth;
//     const height = window.innerHeight;
//     renderer.setSize(width, height);
//     camera.aspect = width / height;
//     camera.updateProjectionMatrix();
//     post_effect.resize(width, height);
// });

export default Sketch;
